import OlMap from 'ol/Map';
import View from 'ol/View';
import Overlay from 'ol/Overlay';
import Feature from 'ol/Feature';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import Cluster from 'ol/source/Cluster';
import {Point, Polygon} from 'ol/geom';
import {createEmpty, extend} from 'ol/extent';
import {createBasemapLayer} from '../../../dashboardsComponents/shared/map/basemaps';
import createMapInteractions from '../../../dashboardsComponents/shared/map/createMapInteractions';
import {buildAreaStyle, buildPointStyle, createClusterStyle, createCoords} from '../../../dashboardsComponents/shared/map/mapStyles';
import {countHtml, escapeHtml, formatValue, previousText} from '../../render/format';
import {isLowBase} from '../../render/lowBase';
import {Cube, MarkHandle, RenderContext} from '../../types';
import {clusterSummary, MapFeature, MapFeatureRow, MapModel} from '../map';

const DEFAULT_CLUSTER_DISTANCE = 40;
const FIT_PADDING = [40, 40, 40, 40];
// Fitting a single point (or tiny area) would zoom to street level
const FIT_MAX_ZOOM = 14;
const EMPTY_CENTER = {longitude: 15.5, latitude: 49.8};
const EMPTY_ZOOM = 4;
const ZOOM_HINT_MS = 700;

const label = (ctx: RenderContext, key: 'mapSummary' | 'mapZoomHint' | 'mapNoLocation'): string => ctx.labels?.[key] ?? key;

function colorWithAlpha(color: string, alpha: number): string {
    const hex = color.match(/^#([0-9a-f]{6})$/i)?.[1];
    if (!hex) {
        return color;
    }
    const [r, g, b] = [0, 2, 4].map(offset => parseInt(hex.slice(offset, offset + 2), 16));

    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function createFeature(data: MapFeature, dark: boolean): Feature {
    const coordinates = data.coords.map(([longitude, latitude]) => createCoords({longitude, latitude}));
    const feature = new Feature({
        geometry: data.kind === 'point' ? new Point(coordinates[0]) : new Polygon([coordinates]),
        data,
    });
    // A low-base member is drawn hollow, like a low-base line point
    const hollow = dark ? '#1E1E1E' : '#FFFFFF';
    if (data.kind === 'point') {
        const args = {
            radius: 7,
            fillColor: data.lowBase ? hollow : data.color,
            strokeColor: data.lowBase ? data.color : 'white',
            strokeWidth: 2,
        };
        const style = buildPointStyle(args, 0);
        // Clusters of one reuse it, so it is not rebuilt on every render
        feature.set('pointStyle', style);
        feature.setStyle(style);
    } else {
        const args = {strokeColor: data.color, strokeWidth: 1.5, fillColor: colorWithAlpha(data.color, data.lowBase ? 0.08 : 0.25)};
        feature.setStyle(buildAreaStyle(args, 0));
    }

    return feature;
}

function rowsHtml(rows: MapFeatureRow[], cube: Cube, ctx: RenderContext): string {
    return rows.map(({measure, cell}) => {
        const previous = previousText(cell, measure, ctx);

        return `<div class="data"><span class="data__label">${escapeHtml(measure.label)}</span>`
            + `<span class="data__value"><b>${formatValue(cell?.value, measure, ctx)}</b> <span class="count">${countHtml(cell, isLowBase(cube, cell))}</span></span>`
            + (previous ? `<span class="data__previous">${previous}</span>` : '')
            + '</div>';
    }).join('');
}

function featureHtml(feature: MapFeature, cube: Cube, ctx: RenderContext): string {
    return `<div class="feature"><h3><span class="feature__dot" style="background-color:${escapeHtml(feature.color)}"></span>`
        + `<span>${escapeHtml(feature.label)}</span></h3>${rowsHtml(feature.rows, cube, ctx)}</div>`;
}

export default function createMapView(
    el: HTMLElement,
    model: MapModel,
    cube: Cube,
    options: Record<string, any>,
    ctx: RenderContext,
): MarkHandle {
    const zoomHintText = label(ctx, 'mapZoomHint').replace('{key}', '⌘ / CTRL');

    el.innerHTML = `<div class="map-container v2-map${ctx.dark ? ' map-container--dark' : ''}">
        <div class="v2-map__canvas"></div>
        <div class="map-zoom-hint">${escapeHtml(zoomHintText)}</div>
        <div class="map-popup"><a class="map-popup__closer" href="#" role="button"></a><div class="map-popup__content"></div></div>
    </div>`;
    const canvas = el.querySelector<HTMLElement>('.v2-map__canvas')!;
    const zoomHint = el.querySelector<HTMLElement>('.map-zoom-hint')!;
    const popup = el.querySelector<HTMLElement>('.map-popup')!;
    const popupContent = el.querySelector<HTMLElement>('.map-popup__content')!;

    if (model.skipped > 0) {
        const notice = document.createElement('div');
        notice.className = 'v2-map__notice small text-muted';
        notice.textContent = label(ctx, 'mapNoLocation').replace('{count}', String(model.skipped));
        el.querySelector('.v2-map')!.appendChild(notice);
    }

    const areaSource = new VectorSource();
    const pointSource = new VectorSource();
    const clustering = options.cluster !== false;
    const clusterSource = clustering
        ? new Cluster({distance: options.clusterDistance ?? DEFAULT_CLUSTER_DISTANCE, source: pointSource})
        : null;

    const extent = createEmpty();
    for (const data of model.features) {
        const feature = createFeature(data, ctx.dark);
        (data.kind === 'point' ? pointSource : areaSource).addFeature(feature);
        extend(extent, feature.getGeometry()!.getExtent());
    }

    const pointLayer = new VectorLayer({
        source: clusterSource ?? pointSource,
        style: clusterSource
            ? feature => {
                const inner = feature.get('features') as Feature[];
                return inner.length === 1 ? inner[0].get('pointStyle') : createClusterStyle(inner.length);
            }
            : undefined,
    });

    const overlay = new Overlay({element: popup, autoPan: {animation: {duration: 250}}});
    const map = new OlMap({
        layers: [createBasemapLayer(), new VectorLayer({source: areaSource}), pointLayer],
        interactions: createMapInteractions(),
        overlays: [overlay],
        target: canvas,
        view: new View({center: createCoords(EMPTY_CENTER), zoom: EMPTY_ZOOM}),
    });

    const view = map.getView();
    const autoFit = model.features.length > 0 && typeof options.zoom !== 'number';
    let fitting = false;
    let viewMoved = false;
    const fitToFeatures = (): void => {
        fitting = true;
        view.fit(extent, {padding: FIT_PADDING, maxZoom: FIT_MAX_ZOOM, size: map.getSize() ?? [canvas.clientWidth || 600, canvas.clientHeight || 400]});
        fitting = false;
    };
    if (model.features.length) {
        if (autoFit) {
            fitToFeatures();
        } else {
            view.setCenter([(extent[0] + extent[2]) / 2, (extent[1] + extent[3]) / 2]);
            view.setZoom(options.zoom);
        }
    }
    view.on(['change:center', 'change:resolution'], () => {
        viewMoved = viewMoved || !fitting;
    });
    // OL tracks window resizes itself; the view follows the container until the user takes over
    map.on('change:size', () => {
        if (autoFit && !viewMoved) {
            fitToFeatures();
        }
    });

    const hidePopup = (): void => {
        overlay.setPosition(undefined);
        popup.style.display = 'none';
        popup.classList.remove('map-popup--cluster');
    };
    const showPopup = (coordinate: number[], html: string, isCluster: boolean): void => {
        popupContent.innerHTML = html;
        popup.style.display = 'block';
        popup.classList.toggle('map-popup--cluster', isCluster);
        overlay.setPosition(coordinate);
    };
    popup.querySelector('.map-popup__closer')!.addEventListener('click', event => {
        event.preventDefault();
        hidePopup();
    });

    map.on('click', event => {
        const clustered: MapFeature[] = [];
        const single: MapFeature[] = [];
        map.forEachFeatureAtPixel(event.pixel, hit => {
            const inner = hit.get('features') as Feature[] | undefined;
            (inner ? clustered : single).push(...(inner ?? [hit as Feature]).map(f => f.get('data') as MapFeature));
        });

        if (clustered.length > 1) {
            const summary = clusterSummary(clustered, cube.measures);
            showPopup(event.coordinate, `<div class="cluster-aggregate">`
                + `<div class="cluster-aggregate__head"><span class="cluster-aggregate__title">${escapeHtml(label(ctx, 'mapSummary'))}</span>`
                + `<span class="cluster-aggregate__count"><i class="bi bi-geo-alt-fill"></i> ${clustered.length}</span></div>`
                + `<div class="cluster-aggregate__body">${rowsHtml(summary, cube, ctx)}</div></div>`
                + `<div class="cluster-list">${clustered.map(feature => featureHtml(feature, cube, ctx)).join('')}</div>`, true);
            return;
        }
        const features = [...clustered, ...single];
        if (features.length) {
            showPopup(event.coordinate, features.map(feature => featureHtml(feature, cube, ctx)).join(''), false);
        } else {
            hidePopup();
        }
    });

    map.on('pointermove', event => {
        if (!event.dragging) {
            canvas.style.cursor = map.hasFeatureAtPixel(event.pixel) ? 'pointer' : '';
        }
    });

    let hintTimer: number | undefined;
    canvas.addEventListener('wheel', event => {
        if (!event.ctrlKey && !event.metaKey) {
            zoomHint.classList.add('show');
            window.clearTimeout(hintTimer);
            hintTimer = window.setTimeout(() => zoomHint.classList.remove('show'), ZOOM_HINT_MS);
        }
    });

    return {
        resize: () => {
            map.updateSize();
            // Print captures the canvas as painted, so the new size has to be rendered now
            map.renderSync();
        },
        destroy: () => {
            window.clearTimeout(hintTimer);
            map.setTarget(undefined);
            map.dispose();
        },
    };
}
